/** @jsx jsx */
/** @jsxFrag React.Fragment */
import * as React from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { css, jsx } from '@compiled/react';
import { LazySuspense } from 'react-loosely-lazy';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { HELP_CENTERS_MANAGEMENT_BASE_PATH, ScreenName } from '@helpcenter/constants';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { editingHelpCenter } from '@helpcenter/help-center-preview';
import type { State } from 'state';
import { getCanAdministerJIRA } from 'state/selectors/user';
import {
	LazyPushingCustomizeSidebar,
	useCustomiseSidebarTriggerRef,
} from 'view/common/pushing-customize-sidebar';
import { LazyHelpCenterBucketsRoutes } from 'view/help-center-customize-sidebar/bucket-routes';

import { HubCustomizeMenu } from '../hub-customize-menu';
import type { HubCustomizeMenuItem, HubCustomizeMenuSection } from '../hub-customize-menu';
import { getHelpCenterAri } from '@helpcenter/meta';
import { parseHelpCenterAri } from '../../../utils/ari-parser';
import hubMessages from '../messages';
import { useHelpCenterCustomizeSection } from '../use-help-center-customize-section';
import { useHubCustomizationSidebar } from '../use-hub-customization-sidebar';

interface HubHomepageCustomizeNavActionsProps {
	iconColor?: string;
}

interface StateProps {
	canAdministerJIRA: boolean;
}

interface DispatchProps {
	setEditingHelpCenter: typeof editingHelpCenter;
}

const sidebarContentStyles = css({
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
});

const noop = () => undefined;

const HubHomepageCustomizeNavActionsComponent = ({
	iconColor,
	canAdministerJIRA,
	setEditingHelpCenter,
}: HubHomepageCustomizeNavActionsProps & StateProps & DispatchProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const triggerRef = useCustomiseSidebarTriggerRef();
	const { isOpen, openFromMenu, close } = useHubCustomizationSidebar('login-announcement');
	const isSidebarOpen = canAdministerJIRA && isOpen;
	const helpCenterSection = useHelpCenterCustomizeSection(canAdministerJIRA);

	React.useEffect(() => {
		setEditingHelpCenter(isSidebarOpen);
	}, [isSidebarOpen, setEditingHelpCenter]);

	React.useEffect(
		() => () => {
			setEditingHelpCenter(false);
		},
		[setEditingHelpCenter],
	);

	React.useEffect(() => {
		if (isOpen && !canAdministerJIRA) {
			close();
		}
	}, [canAdministerJIRA, close, isOpen]);

	const handleLoginAnnouncementClick = React.useCallback(() => {
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'customizeMenu',
			actionSubjectId: 'navbarLoginAnnouncement',
			analyticsType: UI_EVENT_TYPE,
		}).fire();
		openFromMenu();
	}, [createAnalyticsEvent, openFromMenu]);

	const items = React.useMemo<HubCustomizeMenuItem[]>(() => {
		if (!canAdministerJIRA) {
			return [];
		}

		const { helpCenterId } = parseHelpCenterAri(getHelpCenterAri());

		return [
			{
				label: formatMessage(hubMessages.editHelpCenterLabel),
				testId: 'edit-hub-link',
				href: `${HELP_CENTERS_MANAGEMENT_BASE_PATH}/${helpCenterId}/edit-hub`,
				// Same id this item carried in the avatar menu, so its click
				// analytics survive the move.
				actionSubjectId: 'navbarCustomiseHub',
			},
			{
				label: formatMessage(hubMessages.loginAnnouncementButtonLabel),
				testId: 'login-announcement-button',
				onClick: handleLoginAnnouncementClick,
			},
		];
	}, [canAdministerJIRA, formatMessage, handleLoginAnnouncementClick]);

	const sections = React.useMemo(
		() =>
			[{ items }, helpCenterSection].filter((section): section is HubCustomizeMenuSection =>
				Boolean(section),
			),
		[items, helpCenterSection],
	);

	return (
		<>
			<HubCustomizeMenu sections={sections} iconColor={iconColor} />
			{isSidebarOpen ? (
				<ErrorBoundary
					packageName={ScreenName.HELP_CENTER_CUSTOMIZE_LOGIN_ANNOUNCEMENT_SIDEBAR}
					id="hub-login-announcement-sidebar"
					renderError={() => null}
				>
					<LazySuspense fallback={null}>
						<LazyPushingCustomizeSidebar
							isOpen={isSidebarOpen}
							onOpen={noop}
							onClose={close}
							triggerRef={triggerRef}
						>
							<div css={sidebarContentStyles}>
								<LazyHelpCenterBucketsRoutes isOpen={isSidebarOpen} onClose={close} />
							</div>
						</LazyPushingCustomizeSidebar>
					</LazySuspense>
				</ErrorBoundary>
			) : null}
		</>
	);
};

export const HubHomepageCustomizeNavActions = connect(
	(state: State): StateProps => ({
		canAdministerJIRA: getCanAdministerJIRA(state),
	}),
	{ setEditingHelpCenter: editingHelpCenter },
)(HubHomepageCustomizeNavActionsComponent);
